import React, { useEffect, useState } from 'react';
import { employeeApi } from '../services/employeeApi';

interface LeaveHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface LeaveHistory {
  id: number;
  type: 'LEAVE' | 'RETURN' | 'RESIGNATION' | 'CANCEL_RESIGNATION';
  effectiveDate: string;
  expectedReturnDate?: string;
  reason?: string;
  payType?: 'paid' | 'unpaid';
  memo?: string;
  previousStatus: string;
  newStatus: string;
  createdAt: string;
  employee: {
    id: number;
    name: string;
    empNo: string;
    department: string;
    position: string;
  };
}

const LeaveHistoryModal: React.FC<LeaveHistoryModalProps> = ({ isOpen, onClose }) => {
  const [historyList, setHistoryList] = useState<LeaveHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchHistory();
    }
  }, [isOpen]);

  const fetchHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await employeeApi.getAllLongTermLeaveHistory();
      setHistoryList(data);
    } catch (err) {
      setError('히스토리를 불러오는데 실패했습니다.');
      console.error('Failed to fetch leave history:', err);
    } finally {
      setLoading(false);
    }
  };

  const getTypeLabel = (type: 'LEAVE' | 'RETURN' | 'RESIGNATION' | 'CANCEL_RESIGNATION') => {
    const labels = {
      'LEAVE': '휴직',
      'RETURN': '복직',
      'RESIGNATION': '퇴사',
      'CANCEL_RESIGNATION': '퇴사취소'
    };
    return labels[type] || type;
  };

  const getTypeColor = (type: 'LEAVE' | 'RETURN' | 'RESIGNATION' | 'CANCEL_RESIGNATION') => {
    const colors = {
      'LEAVE': 'bg-yellow-100 text-yellow-800',
      'RETURN': 'bg-green-100 text-green-800',
      'RESIGNATION': 'bg-red-100 text-red-800',
      'CANCEL_RESIGNATION': 'bg-blue-100 text-blue-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const getReasonLabel = (reason?: string) => {
    const reasonMap: { [key: string]: string } = {
      '출산휴가': '출산휴가',
      '육아휴직': '육아휴직',
      '질병휴직': '질병휴직',
      '기타': '기타',
    };
    return reason ? reasonMap[reason] || reason : '-';
  };

  const getPayTypeLabel = (payType?: 'paid' | 'unpaid') => {
    return payType === 'paid' ? '유급' : payType === 'unpaid' ? '무급' : '-';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ko-KR');
  };

  const calculateDuration = (startDate: string, endDate?: string) => {
    if (!endDate) return '-';
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return `${diffDays}일`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black bg-opacity-40"
        onClick={onClose}
      ></div>
      <div className="w-full max-w-6xl max-h-[90vh] bg-white rounded-xl shadow-2xl relative z-50 flex flex-col">
        <header className="p-4 border-b border-slate-200 flex justify-between items-center">
          <h3 className="font-bold text-lg">휴직/복직 히스토리</h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="text-slate-500">로딩 중...</div>
            </div>
          ) : error ? (
            <div className="flex justify-center items-center py-12">
              <div className="text-red-500">{error}</div>
            </div>
          ) : historyList.length === 0 ? (
            <div className="flex justify-center items-center py-12">
              <div className="text-slate-400">히스토리가 없습니다.</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      구분
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      직원
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      부서
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      직급
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      발생일
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      사유
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      복직예정일
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      기간
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      급여
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      메모
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {historyList.map((history) => (
                    <tr key={history.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(history.type)}`}>
                          {getTypeLabel(history.type)}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm font-medium text-slate-900">{history.employee.name}</div>
                        <div className="text-sm text-slate-500">{history.employee.empNo}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-500">
                        {history.employee.department}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-500">
                        {history.employee.position}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-900">
                        {formatDate(history.effectiveDate)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-500">
                        {history.type === 'LEAVE' ? getReasonLabel(history.reason) : '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-500">
                        {history.expectedReturnDate ? formatDate(history.expectedReturnDate) : '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-500">
                        {history.type === 'LEAVE' && history.expectedReturnDate
                          ? calculateDuration(history.effectiveDate, history.expectedReturnDate)
                          : '-'
                        }
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-500">
                        {history.type === 'LEAVE' ? getPayTypeLabel(history.payType) : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-500">
                        <div className="max-w-xs truncate" title={history.memo || '-'}>
                          {history.memo || '-'}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <footer className="p-4 bg-slate-50 border-t border-slate-200 flex justify-between items-center">
          <div className="text-sm text-slate-500">
            총 {historyList.length}건
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
          >
            닫기
          </button>
        </footer>
      </div>
    </div>
  );
};

export default LeaveHistoryModal;
